/**
 *
 *  Copyright (c) "Neo4j"
 *  Neo4j Sweden AB [http://neo4j.com]
 *
 *  This file is part of Neo4j.
 *
 *  Neo4j is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, either version 3 of the License, or
 *  (at your option) any later version.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
import classNames from 'classnames';
import type React from 'react';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';

import { CleanIconButton } from '../clean-icon-button';
import { ChevronDownIconOutline, ChevronUpIconOutline } from '../icons';
import { type PolymorphicCommonProps } from './types';

export interface CodeBlockWrapperProps {
  maxHeight?: number;
  code: string;
  heading?: string | React.ReactNode;
  children: React.ReactNode;
  isDisabled?: boolean;
  actions?: React.ComponentPropsWithoutRef<typeof CleanIconButton<'button'>>[];
  shouldShowExpandButton: boolean;
  setShouldShowExpandButton: React.Dispatch<React.SetStateAction<boolean>>;
  className?: string;
  style?: React.CSSProperties;
}

const defaultElement = 'div';

export const CodeBlockWrapper = <
  T extends React.ElementType = typeof defaultElement,
>({
  as,
  maxHeight,
  code,
  heading,
  children,
  isDisabled,
  actions,
  shouldShowExpandButton,
  setShouldShowExpandButton,
  className,
  style,
  ref,
  htmlAttributes,
  ...restProps
}: PolymorphicCommonProps<T, CodeBlockWrapperProps>) => {
  const Component: React.ElementType = as ?? defaultElement;

  const syntaxHighlighterWrapperRef = useRef<HTMLDivElement>(null);

  const [containerHeight, setContainerHeight] = useState(`${maxHeight}px`);
  const [isExpanded, setIsExpanded] = useState(maxHeight === undefined);

  useEffect(() => {
    if (
      (syntaxHighlighterWrapperRef.current?.scrollHeight ?? 0) <=
      (syntaxHighlighterWrapperRef.current?.clientHeight ?? 0)
    ) {
      setIsExpanded(true);
    } else {
      setIsExpanded(false);
    }
  }, []);

  useLayoutEffect(() => {
    if (maxHeight === undefined) {
      return;
    }

    if (
      (syntaxHighlighterWrapperRef.current?.clientHeight ?? 0) >=
      (syntaxHighlighterWrapperRef.current?.scrollHeight ?? 0)
    ) {
      setContainerHeight(`fit-content`);
      setShouldShowExpandButton(false);
    } else {
      setContainerHeight(`${maxHeight}px`);
      setShouldShowExpandButton(true);
    }
  }, [maxHeight, code, setShouldShowExpandButton]);

  return (
    <Component
      ref={ref}
      className={classNames('ndl-code-block-container', className)}
      style={{ height: containerHeight, ...style }}
      {...restProps}
      {...htmlAttributes}
    >
      <div className="n-flex n-flex-col n-h-full">
        {Boolean(heading) === true && (
          <div
            className={classNames('ndl-code-block-title', {
              'ndl-disabled': isDisabled,
            })}
            style={{
              maxWidth: `calc(100% - ${(actions?.length ?? 0) * 36 + 20}px)`,
            }}
            data-testid="ndl-code-block-title"
          >
            {heading}
          </div>
        )}
        <div
          className={classNames('ndl-code-content-container', {
            'ndl-disabled': isDisabled,
          })}
          style={{
            height:
              Boolean(heading) === true ? 'calc(100% - 24px - 12px)' : '100%',
          }}
        >
          {/* Pseudo element on the left to steal the editor focus when clicking outside */}
          <div className="ndl-code-pseudo-element"></div>
          <div
            ref={syntaxHighlighterWrapperRef}
            className="ndl-highlight-wrapper"
            role="textbox"
            aria-label="Code snippet"
            tabIndex={0}
          >
            {children}
          </div>
          {/* Pseudo element on the right to steal the editor focus when clicking outside */}
          <div className="ndl-code-pseudo-element"></div>
        </div>
      </div>
      <div
        className={classNames('ndl-code-block-actions', {
          'ndl-disabled': isDisabled,
        })}
      >
        {actions?.map((iconButtonProps, i) => {
          const { htmlAttributes, description, ...restCleanIconButtonProps } =
            iconButtonProps;
          return (
            <CleanIconButton
              key={i}
              description={`${description ?? 'CodeBlock Action'}`}
              isDisabled={isDisabled}
              htmlAttributes={{
                'data-testid': `ndl-action-button-${i}`,
                ...htmlAttributes,
              }}
              {...restCleanIconButtonProps}
            />
          );
        })}
      </div>
      {shouldShowExpandButton && (
        <div className="ndl-code-block-expand-button">
          <CleanIconButton
            description={isExpanded ? 'Collapse' : 'Expand'}
            tooltipProps={{
              root: {
                shouldCloseOnReferenceClick: true,
              },
            }}
            onClick={() => {
              if (isExpanded) {
                setContainerHeight(`${maxHeight}px`);
                setIsExpanded(false);
              } else {
                setContainerHeight(`fit-content`);
                setIsExpanded(true);
              }
            }}
          >
            {isExpanded ? <ChevronUpIconOutline /> : <ChevronDownIconOutline />}
          </CleanIconButton>
        </div>
      )}
    </Component>
  );
};
